"use client"

import { BlockchainsMoleculeReatom } from "@/app/blockchains/blockchain-molecule.reatom"
import { ApplicationEnvironmentMolecule } from "@/app/env/store"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import { createReatomEvmContainer } from "@rarible_int/anaconda_sdk/ethereum"
import { wrap } from "@reatom/core"
import { useWrap } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { useEffect } from "react"
import { isAddress } from "viem"
import { useAccount, useWalletClient } from "wagmi"
import {
  getPublicClient as wagmiGetPublicClient,
  getWalletClient as wagmiGetWalletClient,
} from "wagmi/actions"
import { createPrivyWagmiConfig } from "../../blockchains/wagmi-adapter"
import { EvmSdkContainerMoleculeReatom } from "./evm-sdk-container-molecule.reatom"

export function useEvmSdkProviderReatom() {
  useRegularWalletSdk()
  return null
}

function useRegularWalletSdk() {
  const { evmNetworkMap } = useMolecule(BlockchainsMoleculeReatom)
  const networks = evmNetworkMap()
  const { updateSdk, clearSdk } = useMolecule(EvmSdkContainerMoleculeReatom)
  const { bffBaseUrl } = useMolecule(ApplicationEnvironmentMolecule)
  const { evmAddress } = useMolecule(WalletConnectMoleculeReatom)
  const profileAddress = evmAddress()
  const { isConnected } = useAccount()
  const { data: walletClient } = useWalletClient()
  const walletChainId = walletClient?.chain?.id

  const resetSdk = useWrap(() => clearSdk())
  const initSdk = useWrap(async (isCurrent: () => boolean) => {
    // A linked Profile address is read-only until its exact signer connects.
    if (
      !isConnected ||
      !profileAddress ||
      !walletClient ||
      walletClient.account.address.toLowerCase() !==
        profileAddress.toLowerCase()
    ) {
      return
    }

    const config = createPrivyWagmiConfig()
    if (!config) return

    // Preserve the Rabby recovery path for wallet clients lacking a chain.
    const targetChainId = walletChainId ?? 1
    const walletClientToUse = walletChainId
      ? walletClient
      : await wrap(wagmiGetWalletClient(config, { chainId: targetChainId }))

    // Recovery must not silently substitute another account or publish after
    // logout, Profile changes, chain changes or a newer initialization.
    if (
      !isCurrent() ||
      evmAddress()?.toLowerCase() !== profileAddress.toLowerCase() ||
      !walletClientToUse ||
      walletClientToUse.account.address.toLowerCase() !==
        profileAddress.toLowerCase() ||
      walletClientToUse.chain?.id !== targetChainId
    ) {
      return
    }

    const chainConfig = networks[targetChainId]
    if (!chainConfig) return
    const exchangeV2Address = chainConfig.exchange.v2
    const bulkExchangeAddress = chainConfig.exchange.wrapper
    if (
      !isAddress(exchangeV2Address, { strict: false }) ||
      !isAddress(bulkExchangeAddress, { strict: false })
    )
      return

    const publicClient = wagmiGetPublicClient(config, {
      chainId: targetChainId,
    })
    if (!publicClient) return

    const sdk = createReatomEvmContainer({
      configs: networks,
      walletClient: walletClientToUse,
      publicClient,
      chainId: targetChainId,
      exchangeV2Address,
      bulkExchangeAddress,
      transferProxies: chainConfig.transferProxies,
      feesVersion: chainConfig.feesVersion,
      treasury: chainConfig.treasury,
      primaryCurrency: chainConfig.primaryCurrency,
      wrappedCurrency: chainConfig.wrappedCurrency,
      marketplaceMarker: "0x000000000000000000000000000000000000000000000001",
      bffBaseUrl,
    })
    updateSdk(sdk, "regular-wallet")
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: useWrap callbacks are stable; these values invalidate the captured SDK identity/configuration.
  useEffect(() => {
    let current = true
    resetSdk()
    void initSdk(() => current).catch(
      wrap((error: Error) => {
        if (current) console.error("Failed to initialize EVM SDK", error)
      }),
    )
    return () => {
      current = false
      resetSdk()
    }
  }, [
    walletClient,
    walletChainId,
    networks,
    isConnected,
    profileAddress,
    bffBaseUrl,
    initSdk,
    resetSdk,
  ])
}
